import styled from '@emotion/styled';
import {
  Button,
  Drawer,
  IconButton,
  Input,
  Select,
  Typography,
} from '@innovamat/glimmer-components';
import { STANDARD_OPTIONS } from '@innovamat/radiance-utils';
import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDisplayRegions } from '../hooks/use-display-regions';
import { useGetPrintingToolModel } from '../hooks/use-get-printing-tool-model';
import type { PrintingToolConfigurationType } from '../state/printing-tool-configuration-state';
import {
  DEFAULT_CONFIGURATION,
  usePrintingToolStore,
} from '../state/printing-tool-configuration-state';
import { CheckboxSection } from './checkbox-section';
import { CustomCheckBox } from './custom-checkbox';

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 50dvw;
  max-width: 585px;
  padding: 16px;
`;

const Divider = styled.div`
  margin-top: 8px;
  width: 100%;
  height: 0px;
  border-bottom: 1px solid #ededed;
  max-width: 100%;
`;

const PrintingToolButtonWrapper = styled.div<{ drawerOpen: boolean }>`
  position: fixed;
  left: ${({ drawerOpen }) =>
    drawerOpen ? 'min(calc(50dvw + 16px), calc(585px + 16px))' : '16px'};
  top: 50%;
  transform: translateY(-50%);
  z-index: 35;
  transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PrintingToolButtonContainer = styled.div`
  position: relative;
`;

const FiltersCounter = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: -10px;
  right: -8px;
  z-index: 36;
  height: 24px;
  width: 24px;
  font-size: 8px;
  border: 1px solid
    ${({ theme }) => theme.tokens.color.global.neutral['neutral300'].value};
  border-radius: 50%;
  background-color: black;
  color: white;
  font-weight: 600;
`;

const PrintingToolButton = styled(IconButton)<{ drawerOpen: boolean }>`
  background-color: ${({ theme }) => theme.tokens.color.global.white.value};
  border: 1px solid
    ${({ theme }) => theme.tokens.color.global.neutral['neutral300'].value};
  z-index: 36;
  transform: rotate(${({ drawerOpen }) => (drawerOpen ? 180 : 0)}deg);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SelectContainer = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StandardCheckboxes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const RegionAndLanguageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  > div {
    width: 100%;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const BlockOptions = [
  { label: 'Content Blocks', value: 'contentBlocks' },
  { label: 'Dimensions', value: 'dimensions' },
  { label: 'SMC', value: 'smc' },
  { label: 'SMP', value: 'smp' },
  { label: 'Big Ideas', value: 'bigIdeas' },
];

const CommonAttributes = [
  { label: 'Assessment', value: 'assessment' },
  { label: 'Materials', value: 'materials' },
];

const courseOrdersOptions = [
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  { label: '9', value: '9' },
  { label: '10', value: '10' },
  { label: '11', value: '11' },
  { label: '12', value: '12' },
];

type ConfigurationState = PrintingToolConfigurationType;

function PrintingToolConfiguration(): JSX.Element {
  const { data } = useGetPrintingToolModel();
  const { regions, languagesToOptions } = useDisplayRegions();
  const { configuration, updateConfiguration } = usePrintingToolStore();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { i18n } = useTranslation();

  const [config, setConfig] = useState<ConfigurationState>({
    guideIds: configuration.guideIds,
    region: configuration.region,
    language: configuration.language,
    blockOptions: configuration.blockOptions,
    commonAttributes: configuration.commonAttributes,
    commonTips: configuration.commonTips,
    signedTips: configuration.signedTips,
    standard: configuration.standard,
    courseOrder: configuration.courseOrder,
    acceptedTypes: configuration.acceptedTypes,
    autoBreakPage: configuration.autoBreakPage,
  });

  const isApplyConfigEnabled =
    config.region &&
    config.language &&
    config.courseOrder &&
    config.standard &&
    config.guideIds.length > 0;

  const activeElements =
    config.blockOptions.length +
    config.commonAttributes.length +
    config.commonTips.length +
    config.signedTips.length;

  const handleConfigurationChangeApply = (): void => {
    updateConfiguration({
      ...config,
      guideIds: config.guideIds,
      region: config.region,
      language: config.language,
      blockOptions: config.blockOptions,
      commonAttributes: config.commonAttributes,
      commonTips: config.commonTips,
      signedTips: config.signedTips,
      standard: config.standard,
      acceptedTypes: {
        commonTips: data?.commonTips.enumValues.map((tip) => tip.name) ?? [],
        signedTips:
          data?.signedTipOptions.enumValues.map((tip) => tip.name) ?? [],
      },
    });
    if (config.language) {
      i18n.changeLanguage(config.language);
    }
  };

  const handleResetConfiguration = (): void => {
    setConfig({
      ...DEFAULT_CONFIGURATION,
      region: null,
      language: null,
      standard: null,
    });
    updateConfiguration(DEFAULT_CONFIGURATION);
  };

  const updateConfig = (updates: Partial<ConfigurationState>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const queryState = queryClient.getQueriesData({
        queryKey: ['Resource'],
      });

      const data = queryState.map(([_, data]) => {
        return !!data;
      });

      setIsLoading(!data.every((item) => item === true));
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  return (
    <>
      <PrintingToolButtonWrapper drawerOpen={open}>
        <PrintingToolButtonContainer>
          {!open && activeElements > 0 && (
            <FiltersCounter>
              <Typography.Body1>
                <Typography.Body3>{activeElements}</Typography.Body3>
              </Typography.Body1>
            </FiltersCounter>
          )}
          <PrintingToolButton
            onClick={() => setOpen(!open)}
            icon={'NextIcon'}
            drawerOpen={open}
          />
        </PrintingToolButtonContainer>
      </PrintingToolButtonWrapper>
      <Drawer
        closeOnClickOutside
        open={open}
        onClose={() => setOpen(false)}
        position="left"
        roundedBorder
      >
        <DrawerContent>
          <Typography.H3>Print Configuration</Typography.H3>
          <Content>
            <Input
              labelText="Guide IDs (comma separated)"
              descriptiveText={`Enter the guide IDs you want to print.`}
              value={config.guideIds.join(',')}
              onChange={(e) => {
                updateConfig({ guideIds: e.target.value.split(',') });
              }}
            />
            <RegionAndLanguageContainer>
              <Select
                key={`region-${config.region}`}
                onChange={(option) => {
                  updateConfig({ region: option?.value });
                  updateConfig({ language: null });
                }}
                options={regions}
                labelText="Select Region"
                value={config.region}
              />
              <Select
                key={`language-${config.language}`}
                status={!config.region ? 'disabled' : undefined}
                onChange={(option) => updateConfig({ language: option?.value })}
                options={languagesToOptions(config.region)}
                labelText="Select Language"
                value={config.language}
              />
            </RegionAndLanguageContainer>

            <SelectContainer>
              <RegionAndLanguageContainer>
                <Select
                  key={`standard-${config.standard}`}
                  onChange={(option) =>
                    updateConfig({ standard: option?.value })
                  }
                  options={STANDARD_OPTIONS}
                  labelText="Select Organization Standard"
                  value={config.standard}
                />
                <Select
                  key={`courses-${config.courseOrder}`}
                  onChange={(option) =>
                    updateConfig({
                      courseOrder: parseInt(option?.value ?? '0'),
                    })
                  }
                  options={courseOrdersOptions}
                  labelText="Select Course Order"
                  value={config.courseOrder?.toString()}
                />
              </RegionAndLanguageContainer>
              <StandardCheckboxes>
                {BlockOptions.map((option) => (
                  <CustomCheckBox
                    key={option.value}
                    text={option.label}
                    checked={config.blockOptions.includes(option.value)}
                    onCheck={(checked) => {
                      updateConfig({
                        blockOptions: checked
                          ? [...config.blockOptions, option.value]
                          : config.blockOptions.filter(
                              (blockOption) => blockOption !== option.value
                            ),
                      });
                    }}
                  />
                ))}
              </StandardCheckboxes>
            </SelectContainer>

            <Divider />

            <CheckboxSection
              title="Common Attributes"
              options={CommonAttributes}
              selectedValues={config.commonAttributes}
              onSelectionChange={(values) =>
                updateConfig({ commonAttributes: values })
              }
              getOptionKey={(option) => option.value ?? ''}
              getOptionLabel={(option) => option.label ?? ''}
            />

            <Divider />

            <CheckboxSection
              title="Common Tips"
              options={data?.commonTips.enumValues ?? []}
              selectedValues={config.commonTips}
              onSelectionChange={(values) =>
                updateConfig({ commonTips: values })
              }
              getOptionKey={(option) => option.name ?? ''}
              getOptionLabel={(option) => option.description ?? ''}
            />

            <Divider />

            <CheckboxSection
              title="Signed Tips"
              options={data?.signedTipOptions.enumValues ?? []}
              selectedValues={config.signedTips}
              onSelectionChange={(values) =>
                updateConfig({ signedTips: values })
              }
              getOptionKey={(option) => option.name ?? ''}
              getOptionLabel={(option) => option.description ?? ''}
            />
          </Content>

          <Divider />

          <div>
            <CustomCheckBox
              text="Auto Break Page"
              checked={config.autoBreakPage}
              onCheck={(checked) => {
                updateConfig({ autoBreakPage: checked });
              }}
            />
            <small>
              Se tienen en cuenta varias reglas para aplicar saltos de página
              automáticamente.
            </small>
          </div>
          <Footer>
            <Button
              onClick={handleResetConfiguration}
              variant="tertiary"
              leftIcon="RestartIcon"
            >
              Reset Configuration
            </Button>
            <Button
              loading={isLoading}
              leftIcon="SettingsIcon"
              onClick={handleConfigurationChangeApply}
              disabled={!isApplyConfigEnabled || isLoading}
            >
              Apply Configuration
            </Button>
          </Footer>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export { PrintingToolConfiguration };
